import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaFilter } from 'react-icons/fa';

const SearchBar = ({ onSearch, onFilterToggle }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  // Mock search suggestions - in a real app, these would come from an API
  const suggestions = [
    'Mediterranean quinoa',
    'Spicy chicken',
    'Vegan dessert',
    'Keto salmon',
    'Italian pizza',
    'Healthy breakfast',
    'Low carb dinner',
    'Gluten free',
    'High protein',
    'Quick meals'
  ];

  const filteredSuggestions = suggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(searchTerm.toLowerCase()) && 
    searchTerm.length > 0
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(value.length > 0);
    
    // Debounce search - in a real app, you might want to use a proper debounce hook
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      onSearch(value);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    onSearch(suggestion);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    onSearch('');
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    onSearch(searchTerm);
    inputRef.current?.blur();
  };

  return (
    <div className="relative" style={{ width: '100%', maxWidth: 720, margin: '0 auto' }} ref={inputRef}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="search-row" style={{ transition: 'transform .2s', transform: isFocused ? 'scale(1.02)' : 'none' }}>
          {/* Search Input */}
          <div className="relative" style={{ flex: 1 }}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                setIsFocused(true);
                if (searchTerm.length > 0) setShowSuggestions(true);
              }}
              placeholder="Search recipes by ingredients, cuisine, or dietary preferences..."
              className="input input-pill search-input"
              style={{ paddingLeft: 16, paddingRight: 44 }}
            />

            {/* Clear Button */}
            {searchTerm && (
              <motion.button initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} type="button" onClick={handleClear} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>
                <FaTimes />
              </motion.button>
            )}
          </div>

          {/* Submit Button */}
          <button type="submit" className="btn btn-cta" style={{ marginLeft: 12, padding: '12px 16px', borderRadius: 12 }}>Search</button>

          {/* Filter Button */}
          {onFilterToggle && (
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} type="button" onClick={onFilterToggle} className="btn btn-settings" style={{ marginLeft: 8, padding: 12, borderRadius: 12 }}>
              <FaFilter />
            </motion.button>
          )}
        </div>
      </form>

      {/* Search Suggestions */}
      <AnimatePresence>
        {showSuggestions && filteredSuggestions.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="card suggestion-list">
            {filteredSuggestions.map((suggestion, index) => (
              <motion.button key={suggestion} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: index * 0.05 }} onClick={() => handleSuggestionClick(suggestion)} className="suggestion-item">
                <span style={{ color: '#374151' }}>
                  {suggestion.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, i) => (
                    <span key={i} style={{ fontWeight: part.toLowerCase() === searchTerm.toLowerCase() ? 700 : 400, color: part.toLowerCase() === searchTerm.toLowerCase() ? 'var(--brand-600)' : undefined }}>
                      {part}
                    </span>
                  ))}
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popular Searches */}
      {isFocused && searchTerm.length === 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="card popular-card">
          <h4 style={{ marginTop: 0, fontSize: 14, color: '#374151' }}>Popular Searches</h4>
          <div className="popular-tags">
            {['Healthy', 'Quick', 'Vegetarian', 'Keto', 'Italian', 'Spicy'].map((tag) => (
              <button key={tag} onClick={() => handleSuggestionClick(tag)} className="btn" style={{ padding: '6px 10px', background: '#f3f4f6', color: '#374151', borderRadius: 999 }}>
                {tag}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SearchBar;
